import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["country", "currency", "localeForCurrency", "listPrice", "discountedPrice", "renderDiscountAddendum", "renderFullPricePrefix", "LowestPriceAddendum", "originalPriceLabel", "salePriceLabel"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import PropTypes from 'prop-types';
import { numberFormat } from '@nike/i18n-core';
import { OriginalPrice, OriginalPriceWithPrefix, FormattedPriceContainer, VisuallyHidden, Break } from './styled';
var PriceLabel = function PriceLabel(_ref) {
  var children = _ref.children;
  return children ? /*#__PURE__*/React.createElement(VisuallyHidden, null, children) : null;
};
PriceLabel.propTypes = {
  children: PropTypes.string
};
var FormattedPrice = function FormattedPrice(_ref2) {
  var country = _ref2.country,
    currency = _ref2.currency,
    localeForCurrency = _ref2.localeForCurrency,
    listPrice = _ref2.listPrice,
    discountedPrice = _ref2.discountedPrice,
    renderDiscountAddendum = _ref2.renderDiscountAddendum,
    renderFullPricePrefix = _ref2.renderFullPricePrefix,
    LowestPriceAddendum = _ref2.LowestPriceAddendum,
    originalPriceLabel = _ref2.originalPriceLabel,
    salePriceLabel = _ref2.salePriceLabel,
    intlOptions = _objectWithoutProperties(_ref2, _excluded);
  var markup = null;
  var lowerCasedCountry = country === null || country === void 0 ? void 0 : country.toLowerCase();
  var isJapan = lowerCasedCountry === 'jp' || false;
  var isIndia = lowerCasedCountry === 'in' || false;
  var isPoland = lowerCasedCountry === 'pl' || false;
  var formattingOptions = _objectSpread(_objectSpread({}, intlOptions), {}, {
    currency: currency
  });
  var formattedListPrice = /*#__PURE__*/React.createElement(FormattedPriceContainer, {
    className: "formatted-price"
  }, /*#__PURE__*/React.createElement(PriceLabel, null, originalPriceLabel), numberFormat(localeForCurrency, listPrice, formattingOptions));
  var indiaFullPrice = /*#__PURE__*/React.createElement(OriginalPriceWithPrefix, {
    key: "sp-opp"
  }, renderFullPricePrefix && renderFullPricePrefix(), formattedListPrice);
  if (!discountedPrice) {
    return isIndia ? indiaFullPrice : formattedListPrice;
  }
  var formattedDiscountPrice = numberFormat(localeForCurrency, discountedPrice, formattingOptions);
  markup = /*#__PURE__*/React.createElement(FormattedPriceContainer, {
    key: "sp-sp",
    className: "sale-price"
  }, /*#__PURE__*/React.createElement(PriceLabel, null, salePriceLabel), formattedDiscountPrice);
  var japanAddendum = /*#__PURE__*/React.createElement("span", {
    key: "sp-sp",
    className: "sale-price"
  }, /*#__PURE__*/React.createElement(PriceLabel, null, salePriceLabel), "".concat(formattedDiscountPrice, " "), renderDiscountAddendum && renderDiscountAddendum());
  var FormattedSalePrice = function FormattedSalePrice() {
    return /*#__PURE__*/React.createElement("span", {
      key: "sp-sp",
      className: "sale-price"
    }, /*#__PURE__*/React.createElement(PriceLabel, null, salePriceLabel), "".concat(formattedDiscountPrice));
  };
  if (typeof localeForCurrency === 'string' && isPoland) {
    return [/*#__PURE__*/React.createElement("s", {
      className: "original-price pl",
      key: "sp-op"
    }, formattedListPrice), /*#__PURE__*/React.createElement(FormattedSalePrice, {
      key: "sp-sp"
    }), /*#__PURE__*/React.createElement(Break, {
      key: "break"
    }), /*#__PURE__*/React.createElement(LowestPriceAddendum, {
      key: "lowest-price-addendum"
    })];
  }

  /* eslint-enable prettier/prettier */
  if (typeof localeForCurrency === 'string' && !isJapan && !isIndia) {
    return [/*#__PURE__*/React.createElement(OriginalPrice, {
      className: "original-price",
      key: "sp-op"
    }, formattedListPrice), markup];
  }
  if (typeof localeForCurrency === 'string' && isJapan) {
    return japanAddendum;
  }
  if (typeof localeForCurrency === 'string' && isIndia) {
    return [/*#__PURE__*/React.createElement(OriginalPriceWithPrefix, {
      className: "original-price",
      key: "sp-op"
    }, renderFullPricePrefix && renderFullPricePrefix(), formattedListPrice), markup];
  }
  return markup;
};
FormattedPrice.propTypes = {
  /**
   * country code, preferred as lowercase. Used for Japan addendum logic.
   */
  country: PropTypes.string.isRequired,
  /**
   * currency code, preferred in caps
   */
  currency: PropTypes.string.isRequired,
  /**
   * the locale value from which to format the specified currency
   */
  localeForCurrency: PropTypes.string.isRequired,
  /**
   * the listPrice of a product (eg from merch/price/v1)
   */
  listPrice: PropTypes.string.isRequired,
  /**
   * a discounted price of a product
   */
  discountedPrice: PropTypes.string,
  /**
   * Discount addendum for Japan sale price
   */
  renderDiscountAddendum: PropTypes.func,
  /**
   * MRP prefix for India full retail price
   */
  renderFullPricePrefix: PropTypes.func,
  /**
   * label for a11y for original price
   */
  originalPriceLabel: PropTypes.string,
  /**
   * label for a11y for sale price
   */
  salePriceLabel: PropTypes.string,
  /**
   * label for lowest price
   */
  LowestPriceAddendum: PropTypes.node
};
export default /*#__PURE__*/React.memo(FormattedPrice);